import React, {useEffect, useMemo, useState} from 'react';
import styled, {css} from 'styled-components';
import {Provider, useDispatch, useStore} from 'react-redux';
import {RouterContext} from "next/dist/shared/lib/router-context";
import {useRouter} from 'next/router';

import {addUserBookmarks, removeUserBookmarks} from '@services/requests';
import AuthModal from '@components/modals/AuthModal';
import {useModal} from '@components/Modal/Modal.hooks';
import {convertThumbnailToPictureImage} from '@tager/web-modules';

import {ShortCodeService} from '@tager/web-core';
import {media} from '@/utils/mixin';
import Picture from '@/components/Picture';
import {getPostDate, shuffleArray} from '@/utils/posts';
import PostMeta from '@/components/PostMeta/PostMeta';
import {PostFull} from '@/typings/model';
import {ReactComponent as BookmarksSvg} from '@/assets/svg/bookmarks.svg';
import {ReactComponent as BookmarksActiveSvg} from '@/assets/svg/bookmarksActive.svg';

import {StoreDispatch, useTypedSelector} from '@/store/store';
import {selectInvestment, selectStock} from '@/store/reducers/pages/post';
import {
  I18nContextProvider,
  useI18nContext,
  useTranslation,
} from '@/components/I18nProvider';

import {
  checkUserBookmarksThunk,
  getBookmarksThunk,
} from '@/store/reducers/pages/bookmarks';
import {selectIsUserAuthorized} from '@/store/reducers/auth';
import WidgetStockInvesting from './WidgetStockInvesting';
import WidgetSamePost from './WidgetSamePost';
import WidgetButton from './WidgetButton';
import OtherPost from './OtherPost';
import WidgetInvestmentIdeas from './WidgetInvestmentIdeas';

type Props = {
  post: PostFull;
};

const postShortCodeService = new ShortCodeService('post', ['id']);
const buttonShortCodeService = new ShortCodeService('button', ['label', 'url']);

function PostPage({post}: Props) {
  const bookmark = useMemo(() => ({id: post.id, alias: post.urlAlias}), [
    post.id,
    post.urlAlias,
  ]);
  const dispatch = useDispatch<StoreDispatch>();
  const [isActive, setActive] = useState<boolean>(!bookmark);
  const store = useStore();
  const router = useRouter();
  const {t} = useTranslation();
  const i18nContext = useI18nContext();
  const openModal = useModal();
  const [content, setContent] = useState<string>(post.body);

  const isUserAuthorized = useTypedSelector((state) =>
    selectIsUserAuthorized(state)
  );
  const stock = useTypedSelector((state) =>
    selectStock(state, post.additionalFields.stock_id)
  );
  const investment = useTypedSelector((state) =>
    selectInvestment(state, post.additionalFields.investment_id)
  );

  useEffect(() => {
    let replacedBody = post.body;
    replacedBody = postShortCodeService.replaceShortCodeByElement(replacedBody);
    replacedBody = buttonShortCodeService.replaceShortCodeByElement(
      replacedBody
    );

    setContent(replacedBody);
  }, [post.body]);

  useEffect(() => {
    buttonShortCodeService.renderComponent(
      (props: { label: string; url: string }) => <WidgetButton {...props} />
    );
    postShortCodeService.renderComponent((widgetProps: { id: string }) => {
      return (
        <RouterContext.Provider value={router}>
          <Provider store={store}>
            <I18nContextProvider value={i18nContext}>
              <WidgetSamePost {...widgetProps} />
            </I18nContextProvider>
          </Provider>
        </RouterContext.Provider>
      );
    });
  }, [i18nContext, router, store, content]);

  useEffect(() => {
    if (isUserAuthorized) {
      dispatch(checkUserBookmarksThunk(bookmark)).then((includesCurrentPost) =>
        setActive(includesCurrentPost)
      );
    }
  }, [bookmark]);

  function toggleBookmark() {
    if (isUserAuthorized) {
      dispatch(checkUserBookmarksThunk(bookmark)).then(
        (includesCurrentPost) => {
          if (includesCurrentPost) {
            removeUserBookmarks(bookmark.id).then((newIsActive) => {
              dispatch(getBookmarksThunk());
              setActive(false);
            });
          } else {
            addUserBookmarks(bookmark.id).then((newIsActive) => {
              dispatch(getBookmarksThunk());

              setActive(true);
            });
          }
        }
      );
    } else {
      openModal(AuthModal, {});
    }
  }

  return (
    <PostContent>
      <Header isImg={!!post.coverImage}>
        {post.coverImage ? (
          <Left>
            <Picture
              laptop={convertThumbnailToPictureImage(post.coverImage.inner)}
              mobileSmall={convertThumbnailToPictureImage(
                post.coverImage.previewMobile
              )}
            />
          </Left>
        ) : null}
        <Right>
          <Title>{post.title}</Title>
          <Excerpt>{post.excerpt}</Excerpt>
          <PostMeta
            date={getPostDate(post.date, t)}
            category={
              post.categories && post.categories.length
                ? post.categories[0].name
                : null
            }
          />
          <Footer>
            <Bookmarks isActive={isActive} onClick={toggleBookmark}>
              {isActive ? <BookmarksActiveSvg/> : <BookmarksSvg/>}
              <TextBookmark isActive={isActive}>
                {isActive ? t('removeBookmarks') : t('addBookmark')}
              </TextBookmark>
            </Bookmarks>
          </Footer>
        </Right>
      </Header>
      <Section>
        <Content
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />

        {investment ? <WidgetInvestmentIdeas investment={investment}/> : null}
        {stock ? <WidgetStockInvesting stock={stock}/> : null}
      </Section>
      {post.relatedPosts.length > 0 ? (
        <SectionOtherPost>
          <TitleOtherPost>{t('OtherArticles')}</TitleOtherPost>
          <PostList>
            {shuffleArray(post.relatedPosts)
              .slice(0, 3)
              .map((relatedPost) => (
                <OtherPost post={relatedPost} key={relatedPost.id}/>
              ))}
          </PostList>
        </SectionOtherPost>
      ) : null}
    </PostContent>
  );
}

const PostContent = styled.div``;

const Header = styled.header<{ isImg: boolean }>`
  display: ${(props) => (props?.isImg ? 'flex' : 'block')};
  flex-direction: ${(props) => (props.isImg ? 'row-reverse' : null)};
  min-height: ${(props) => (props.isImg ? '370px' : 'auto')};
  ${media.tabletLargeOnly(css`
    min-height: auto;
    max-height: 245px;
  `)}
  ${media.tabletSmall(css`
    min-height: auto;
    display: block;
  `)}
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: 700px;
`;

const Title = styled.h1`
  font-size: 42px;
  line-height: 48px;
  margin: 0 0 5px 0;
  font-weight: 700;
  display: block;
  ${media.tablet(css`
    font-size: 35px;
  `)}
  ${media.tabletSmall(css`
    font-size: 32px;
    line-height: 38px;
  `)}
  ${media.mobile(css`
    font-size: 22px;
    line-height: 26px;
  `)}
`;

const Excerpt = styled.p`
  font-size: 18px;
  line-height: 28px;
  margin-bottom: 10px;
  display: block;
`;

const Footer = styled.div`
  padding-bottom: 15px;
  padding-top: 10px;
  margin-top: auto;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const TextBookmark = styled.span<{ isActive: boolean }>`
  color: var(--blue-color);
  font-size: 15px;
  opacity: 0.8;
  transition: 0.2s ease all;
 ${(props) =>
  props.isActive
    ? css`
         opacity: 0.6;
         color: var(--main-text-color);
       `
    : null}
  }
`;

const Bookmarks = styled.button<{ isActive: boolean }>`
  height: 24px;
  line-height: 24px;
  svg {
    vertical-align: sub;
    margin-right: 10px;
    width: 14px;
    height: 18px;
    white-space: nowrap;
    font-size: 15px;
    position: relative;
    transition: 0.2s ease all;
    fill: var(--blue-color);
    opacity: 0.8;
    ${(props) =>
  props.isActive
    ? css`
            opacity: 0.6;
            fill: var(--main-text-color);
          `
    : null}
  }
  &:hover {
    svg {
      opacity: 1;
    }
    ${TextBookmark} {
      opacity: 1;
    }
  }
`;

const Left = styled.div`
  min-width: 750px;
  overflow: hidden;
  position: relative;
  margin: 0 0 0 20px;
  height: 370px;
  background-color: #eee;
  img {
    width: 100%;
  }
  ${media.tabletLargeOnly(css`
    flex-basis: 500px;
    min-width: 500px;
    height: 245px;
  `)}
  ${media.tabletSmall(css`
    margin: auto;
    width: 728px;
    min-width: 728px;
    height: 361px;
    img {
    }
  `)}
  ${media.mobile(css`
    min-height: 206px;
    height: auto;
    margin: 0 -16px 16px;
    width: auto;
    min-width: 0;
    display: block;
  `)}
   ${media.mobileMedium(css`
    min-height: 158px;
    height: auto;
  `)}
`;

const Section = styled.section`
  position: relative;
  font-size: 18px;
  line-height: 30px;
  margin-top: 70px;
  padding-bottom: 40px;
  width: 700px;
  ${media.tabletSmall(css`
    margin-top: 20px;
    padding-bottom: 20px;
    width: auto;
  `)}
  ${media.mobile(css`
    font-size: 15px;
    line-height: 24px;
    padding-bottom: 0;
  `)}
`;
const Content = styled.div`
  word-wrap: break-word;
  h3 {
    margin-bottom: 10px;
    margin-top: 40px;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    + p {
      margin-top: 10px;
    }
  }
  ol {
    margin-left: 45px;
    list-style-type: decimal;
  }
  ul {
    margin-left: 45px;
    list-style-type: none;
    li {
      position: relative;

      &:before {
        content: '—';
        position: absolute;
        left: -27px;
        top: -1px;
      }
    }
  }
  h2 {
    margin-bottom: 10px;
    margin-top: 40px;
    font-size: 32px;
    line-height: 43px;
    + p {
      margin-top: 10px;
    }
    span {
      font-weight: 700 !important;
    }
  }
  p {
    margin: 20px 0;
    + ul,
    + ol {
      margin-top: -15px;
    }
  }
  table {
    margin-bottom: 20px;
    width: 100%;
    font-size: 14px;
    border-spacing: 0;
    border-collapse: collapse;
    ${media.mobile(css`
      width: 100%;
      display: block;
      overflow-x: auto;
    `)}
    tr {
      :first-child td {
        text-align: left;
        border-bottom: 1px solid #cecece;
        span {
          font-weight: 700 !important;
        }
      }
      td {
        padding: 5px 15px 5px 0;
        white-space: nowrap;
        line-height: 19px;
        a {
          text-decoration: underline;
          border-bottom: none;
        }
      }
    }
  }
  a {
    border-bottom: 1px solid var(--static-links-color);
  }
  img {
    height: auto;
  }
  blockquote{
    margin: 20x 0;
    padding-left: 25px;
    position: relative;
    
      @media(max-width: 767px){
        padding-left: 20px;
      }
    &:before{
      content: '';
      position: absolute;
      display: block;
      left: 0;
      top: -5px;
      bottom: -5px;
      width: 5px;
      background: rgba(0,0,0,.3);
      
      @media(max-width: 767px){
        width: 3px;
        top: -3px;
        bottom: -3px;
      }
    }
  }
  .ButtonBlock {
    padding: 20px 0;
  }
  .Button {
    padding: 17px 45px;
    border-radius: 35px;
    background: var(--blue-color);
    color: #fff;
    font-weight: 700;
    font-size: 14px;
    box-shadow: 0 5px 10px 0 #0000002b;
    cursor: pointer;
    &:hover {
      background: #0e5bb6;
    }
  }

  ${media.mobile(css`
    h2 {
      font-size: 22px;
      line-height: 26px;
    }
    p {
      margin: 10px 0;
    }
  `)}
`;
const SectionOtherPost = styled.section``;
const TitleOtherPost = styled.h2`
  display: block;
  margin-bottom: 25px;
  font-size: 32px;
  ${media.mobile(css`
    display: block;
    font-size: 22px;
    line-height: 30px;
  `)}
`;
const PostList = styled.div`
  display: flex;
  margin-bottom: 35px;
  ${media.mobile(css`
    display: block;
  `)}
`;

export default PostPage;
